// @flow
import React, {Fragment, useState} from 'react';
import Experiment, {On} from '@homeaway/react-experiment';
import {debounce} from 'lodash';
import gql from 'graphql-tag';
import {Unveil} from '@homeaway/react-unveil';
import {Link} from '@homeaway/react-localization-components';
import {isEqual, omit} from 'lodash';
import HitCarousel from './HitCarousel/HitCarousel';
import HitInfo from '../HitInfo/HitInfo';
import SeeMorePrompt from '../SeeMorePrompt/SeeMorePrompt';
import {demoteHit} from '../../utils/seeMorePromptUtils';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import {PP_ACCEPTED} from '../../constants/badges';
import {PROPERTY_CARD_CAROUSEL} from '../../utils/carouselEdapUtils';
import type {HitClickEventData} from '../../utils/hitEdapUtils';
import type {SearchParamsType} from '../../reducers/searchParams';
import {hitInfoFragments} from '../HitInfo/HitInfo.fragments';
import type {ListingShape as HitInfoListingShape} from '../HitInfo/HitInfo';
import {VRBO_SERP_SEE_MORE_PROMPT} from '../../abacus/experiments';

import './Hit.less';

export type Arm = {
    level: ?string,
    imageUrl: ?string,
    categoryName: ?string
};
export type Mab = {
    cached: ?boolean,
    banditId: ?string,
    payloadId: ?string,
    campaignId: ?string,
    arm: ?Arm
};

export type BadgeShape = {
    id: string,
    name: string,
    helpText: ?string
};

export type ListingImage = {
    altText: ?string,
    c6_uri: ?string,
    c9_uri: ?string,
    mab: ?Mab
};

export type ListingShape = HitInfoListingShape & {
    headline: ?string,
    virtualTourBadge: ?BadgeShape,
    images: ?(ListingImage[]),
    thumbnailUrl: ?string,
    listingNumber: ?number
};

export type MouseLeaveShape = {
    listing: ListingShape,
    hitIndex: number,
    page?: number,
    pageSize?: number
};

type Props = {
    deviceType?: string,
    havId?: string,
    hitIndex: number,
    isGoodBot?: boolean,
    isWithoutAnchor?: boolean,
    listing: ListingShape,
    onHitClick?: (HitClickEventData) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (MouseLeaveShape) => void,
    onDemoteHit?: (hitIndex: number) => void,
    page?: number,
    pageSize?: number,
    inputParams: SearchParamsType,
    siteName?: string
};

const HIT_UNVEIL_THRESHOLD = 100;
const HIT_UNVEIL_MIN_HEIGHT = '256px';

export const fragments = {
    Listing: gql`
        fragment HitListing on Listing {
            virtualTourBadge {
                name
                id
                helpText
            }
            amenitiesBadges {
                name
                id
                helpText
            }
            images {
                altText
                c6_uri
                c9_uri
                mab {
                    banditId
                    payloadId
                    campaignId
                    cached
                    arm {
                        level
                        imageUrl
                        categoryName
                    }
                }
            }
            ...HitInfoListing
        }
        ${hitInfoFragments.Listing}
    `
};

export const mapHitFragments = {
    Listing: gql`
        fragment MapHitListing on Listing {
            ...HitListing
            geoCode {
                latitude
                longitude
            }
        }
        ${fragments.Listing}
    `
};

export const hitDataErrors = (
    listing: ListingShape,
    isDesktop: boolean
): Array<string> => {
    const errors = [];

    // eslint-disable-next-line guard-for-in
    for (const key in listing) {
        const value = listing[key];

        switch (key) {
            case 'detailPageUrl':
            case 'images':
                if (!value || !value.length) {
                    errors.push(key);
                }
                break;
            case 'propertyMetadata':
                if (isDesktop) {
                    if (!value) {
                        errors.push(key);
                    } else if (!value.headline) {
                        errors.push(`${key}.headline`);
                    }
                }
                break;
            default:
                break;
        }
    }

    return errors;
};

const renderHit = (
    props: Props,
    isHitClicked,
    setIsHitClicked,
    viewedImages,
    setViewedImages,
    showHit,
    setShowHit,
    isModalOpen,
    setModalOpen
) => {
    const {
        onHitClick,
        onMouseEnter,
        onMouseLeave,
        deviceType = '',
        havId = '',
        hitIndex,
        listing,
        page,
        pageSize,
        inputParams,
        siteName = '',
        onDemoteHit = (index) => index
    } = props;

    if (!listing) {
        return null;
    }

    const handleMouseEnter = () =>
        onMouseEnter && onMouseEnter(listing.listingId);
    const handleMouseLeave = () =>
        onMouseLeave && onMouseLeave({listing, hitIndex, page, pageSize});
    const onClick = () => {
        setIsHitClicked(true);
        return (
            onHitClick &&
            onHitClick({
                listing,
                hitIndex,
                page,
                pageSize,
                inputParams,
                havId,
                deviceType,
                siteName
            })
        );
    };

    const onDemote = () => {
        demoteHit(listing.listingNumber);
        setShowHit(false);
        onDemoteHit(hitIndex);
    };

    const rankedBadges = listing.rankedBadges || [];
    const premierPartnerBadge = rankedBadges.find(
        (badge) => badge.id === PP_ACCEPTED
    );
    const showSeeMorePropmt = isHitClicked || viewedImages.length > 2;

    return (
        <Fragment>
            {!!showHit && (
                <div
                    className="Hit"
                    data-wdio="hit"
                    role="listitem"
                    hit-index={hitIndex}
                    onMouseEnter={debounce(handleMouseEnter, 100)}
                    onMouseLeave={debounce(handleMouseLeave, 100)}
                >
                    <Link href={listing.detailPageUrl}>
                        {(localizedUrl) => (
                            <HitCarousel
                                detailPageUrl={localizedUrl}
                                edapCollectionType={PROPERTY_CARD_CAROUSEL}
                                images={listing.images || []}
                                onImageClick={onClick}
                                premierPartnerBadge={premierPartnerBadge}
                                viewedImages={viewedImages}
                                setViewedImages={setViewedImages}
                            />
                        )}
                    </Link>
                    <div className="Hit__tripboardButton">
                        <TripBoardButton
                            expandedButton={false}
                            listing={listing}
                        />
                    </div>
                    <HitInfo
                        onClick={onClick}
                        listing={listing}
                        hitIndex={hitIndex}
                        isModalOpen={isModalOpen}
                        setModalOpen={setModalOpen}
                    />
                </div>
            )}
            {showSeeMorePropmt && (
                <Experiment name={VRBO_SERP_SEE_MORE_PROMPT}>
                    <On>
                        <SeeMorePrompt
                            listingId={listing.listingId}
                            onDemote={onDemote}
                        />
                    </On>
                </Experiment>
            )}
        </Fragment>
    );
};

const Hit = (props: Props) => {
    const [showHit, setShowHit] = useState(true);
    const [isHitClicked, setIsHitClicked] = useState(false);
    const [viewedImages, setViewedImages] = useState([0]);
    const [isModalOpen, setModalOpen] = useState(false);

    // Good Bots (deviceClassification = -25) get Hits with no Unveil
    if (props.isGoodBot) {
        return renderHit(
            props,
            isHitClicked,
            setIsHitClicked,
            viewedImages,
            setViewedImages,
            showHit,
            setShowHit,
            isModalOpen,
            setModalOpen
        );
    }

    // Unveil for the masses
    return (
        <Unveil
            threshold={HIT_UNVEIL_THRESHOLD}
            minContainerHeight={HIT_UNVEIL_MIN_HEIGHT}
        >
            {renderHit(
                props,
                isHitClicked,
                setIsHitClicked,
                viewedImages,
                setViewedImages,
                showHit,
                setShowHit,
                isModalOpen,
                setModalOpen
            )}
        </Unveil>
    );
};

/* istanbul ignore next */
const propsAreEqual = (prev: Props, next: Props) => {
    return isEqual(
        omit(prev, ['onHitClick', 'onMouseEnter', 'onMouseLeave']),
        omit(next, ['onHitClick', 'onMouseEnter', 'onMouseLeave'])
    );
};

export default React.memo<Props>(Hit, propsAreEqual);
